import { usStateOptions } from '../data/options';
import type { RowRecord } from '../types';

export type ValidationMessage = {
  field?: string;
  message: string;
  severity: 'error' | 'warning';
  matchId?: string;
  matchLabel?: string;
};

export type ValidationResult = {
  normalized: RowRecord;
  errors: ValidationMessage[];
  warnings: ValidationMessage[];
};

export type EntityValidationContext = {
  existingRows?: RowRecord[];
  currentId?: string | null;
  requireCompleteInactiveFacilityAddress?: boolean;
};

const validStates = new Set(usStateOptions.map((option) => option.value));
const namePattern = /^[\p{L}\p{M} .'\u2019-]+$/u;
const cityPattern = /^[\p{L}\p{M} .'\u2019-]+$/u;
const punctuationOnlyPattern = /^[\s\p{P}\p{S}\d]+$/u;
const unsafeTextPattern = /<\s*script|javascript:/i;
const safeUrlProtocols = new Set(['http:', 'https:']);
const ntrpValues = new Set(['2.0', '2.5', '3.0', '3.5', '4.0', '4.5', '5.0', '5.5', '6.0', '7.0']);

export const supportedStateValues = [...validStates].sort();
export const supportedProfileUrlDomains = {
  utr: ['app.utrsports.net', 'utrsports.net', 'www.utrsports.net', 'universaltennis.com', 'www.universaltennis.com'],
  tennisRecord: ['tennisrecord.com', 'www.tennisrecord.com'],
  usta: ['usta.com', 'www.usta.com', 'tennislink.usta.com'],
  googleMaps: ['maps.google.com', 'www.google.com', 'google.com', 'maps.app.goo.gl', 'goo.gl'],
};

export function collapseWhitespace(value: unknown): string {
  return String(value ?? '').trim().replace(/\s+/g, ' ');
}

export function normalizeEmail(value: unknown): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  const at = trimmed.lastIndexOf('@');
  if (at < 0) return trimmed;
  return `${trimmed.slice(0, at)}@${trimmed.slice(at + 1).toLowerCase()}`;
}

export function isValidEmailAddress(value: unknown): boolean {
  const email = normalizeEmail(value);
  if (!email) return true;
  if (email.length > 254 || /\s/.test(email)) return false;
  const parts = email.split('@');
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  if (!local || !domain || domain.length > 253) return false;
  if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) return false;
  if (!domain.includes('.')) return false;
  const labels = domain.split('.');
  if (labels.some((label) => !label || label.startsWith('-') || label.endsWith('-') || !/^[a-z0-9-]+$/i.test(label))) return false;
  const suffix = labels[labels.length - 1];
  return /^[a-z]{2,63}$/i.test(suffix);
}

export function normalizePhone(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const digits = raw.replace(/[()\s.-]/g, '');
  const normalizedDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (!/^\d{10}$/.test(normalizedDigits)) return raw;
  return `(${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3, 6)}-${normalizedDigits.slice(6)}`;
}

function phoneDigits(value: unknown): string {
  const raw = String(value ?? '').trim();
  const digits = raw.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

export function isValidUsPhone(value: unknown): boolean {
  const raw = String(value ?? '').trim();
  if (!raw) return true;
  if (/[a-z]/i.test(raw)) return false;
  const digits = phoneDigits(raw);
  if (!/^\d{10}$/.test(digits)) return false;
  return !/^(\d)\1{9}$/.test(digits);
}

export function normalizeZip(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/^\d{9}$/.test(raw)) return `${raw.slice(0, 5)}-${raw.slice(5)}`;
  return raw;
}

export function isValidZip(value: unknown): boolean {
  const zip = normalizeZip(value);
  return !zip || /^\d{5}(-\d{4})?$/.test(zip);
}

export function normalizeState(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

export function isValidState(value: unknown): boolean {
  const state = normalizeState(value);
  return !state || validStates.has(state);
}

export function normalizeUrl(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return raw;
  return `https://${raw}`;
}

export function parseSafeUrl(value: unknown): URL | null {
  const normalized = normalizeUrl(value);
  if (!normalized) return null;
  try {
    const url = new URL(normalized);
    return safeUrlProtocols.has(url.protocol) ? url : null;
  } catch {
    return null;
  }
}

function hostMatches(url: URL, domains: string[]): boolean {
  const host = url.hostname.toLowerCase();
  return domains.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

function validateUrlField(field: string, value: unknown, normalized: RowRecord, errors: ValidationMessage[], warnings: ValidationMessage[]) {
  const raw = String(value ?? '').trim();
  if (!raw) {
    normalized[field] = null;
    return;
  }
  if (/^(javascript|data|file):/i.test(raw)) {
    errors.push({ field, severity: 'error', message: 'Enter a safe http or https URL.' });
    normalized[field] = raw;
    return;
  }
  const url = parseSafeUrl(raw);
  if (!url) {
    errors.push({ field, severity: 'error', message: 'Enter a valid http or https URL.' });
    normalized[field] = normalizeUrl(raw);
    return;
  }
  const normalizedValue = url.toString();
  normalized[field] = normalizedValue;
  if (field === 'utr_profile_url' && !hostMatches(url, supportedProfileUrlDomains.utr)) {
    errors.push({ field, severity: 'error', message: wrongUrlFieldMessage(url) || 'Enter a valid UTR Sports profile URL.' });
  }
  if (field === 'tennis_record_profile_url' && !hostMatches(url, supportedProfileUrlDomains.tennisRecord)) {
    errors.push({ field, severity: 'error', message: wrongUrlFieldMessage(url) || 'Enter a valid TennisRecord profile URL.' });
  }
  if (field === 'usta_profile_url' && !hostMatches(url, supportedProfileUrlDomains.usta)) {
    errors.push({ field, severity: 'error', message: wrongUrlFieldMessage(url) || 'Enter a valid USTA profile URL.' });
  }
  if (field === 'google_maps_url') {
    const isGoogle = hostMatches(url, supportedProfileUrlDomains.googleMaps)
      && (url.hostname.toLowerCase().includes('goo.gl') || url.hostname.toLowerCase().includes('maps.app.goo.gl') || url.pathname.toLowerCase().startsWith('/maps') || url.hostname.toLowerCase() === 'maps.google.com');
    if (!isGoogle) errors.push({ field, severity: 'error', message: 'Enter a valid Google Maps URL.' });
  }
}

function wrongUrlFieldMessage(url: URL): string | null {
  if (hostMatches(url, supportedProfileUrlDomains.tennisRecord)) return 'This appears to be a TennisRecord URL. Enter it in the TennisRecord Profile URL field.';
  if (hostMatches(url, supportedProfileUrlDomains.utr)) return 'This appears to be a UTR URL. Enter it in the UTR Profile URL field.';
  if (hostMatches(url, supportedProfileUrlDomains.usta)) return 'This appears to be a USTA URL. Enter it in the USTA Profile URL field.';
  return null;
}

function normalizedUrlKey(value: unknown): string {
  const url = parseSafeUrl(value);
  if (!url) return '';
  url.hash = '';
  return url.toString().replace(/\/$/, '').toLowerCase();
}

function normalizedNameKey(...parts: unknown[]): string {
  return parts.map((part) => collapseWhitespace(part).toLowerCase()).join('|');
}

function normalizedPhoneKey(value: unknown): string {
  return phoneDigits(value);
}

function hasMeaningfulText(value: unknown): boolean {
  const text = collapseWhitespace(value);
  return Boolean(text) && !punctuationOnlyPattern.test(text);
}

function validateName(field: string, label: string, normalized: RowRecord, errors: ValidationMessage[]) {
  const value = collapseWhitespace(normalized[field]);
  normalized[field] = value || null;
  if (!value) errors.push({ field, severity: 'error', message: `${label} is required.` });
  else if (value.length > 100) errors.push({ field, severity: 'error', message: `${label} must be 100 characters or fewer.` });
  else if (!namePattern.test(value) || punctuationOnlyPattern.test(value)) errors.push({ field, severity: 'error', message: `${label} can include letters, spaces, apostrophes, periods, and hyphens.` });
}

function validateCity(field: string, normalized: RowRecord, errors: ValidationMessage[]) {
  const value = collapseWhitespace(normalized[field]);
  normalized[field] = value || null;
  if (value && (!cityPattern.test(value) || punctuationOnlyPattern.test(value))) {
    errors.push({ field, severity: 'error', message: 'City can include letters, spaces, apostrophes, periods, and hyphens.' });
  }
}

function dateOnly(value: unknown): string {
  return String(value ?? '').trim().slice(0, 10);
}

function birthdayAge(birthday: string): number | null {
  if (!birthday) return null;
  const [year, month, day] = birthday.split('-').map(Number);
  const today = new Date();
  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() + 1 - month;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) age -= 1;
  return age;
}

function isRealDateOnly(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day;
}

function validateRating(field: string, label: string, normalized: RowRecord, errors: ValidationMessage[], min: number, max: number) {
  const raw = normalized[field];
  if (raw === '' || raw === null || raw === undefined) {
    normalized[field] = null;
    return;
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) errors.push({ field, severity: 'error', message: `${label} must be numeric.` });
  else if (value < min || value > max) errors.push({ field, severity: 'error', message: `${label} must be between ${min.toFixed(2)} and ${max.toFixed(2)}.` });
  else if (!/^\d+(\.\d{1,2})?$/.test(String(raw))) errors.push({ field, severity: 'error', message: `${label} can have up to two decimal places.` });
  normalized[field] = Number.isFinite(value) ? value : raw;
}

function duplicateRows(rows: RowRecord[], currentId: string | null | undefined): RowRecord[] {
  return rows.filter((row) => String(row.id || '') !== String(currentId || '') && row.deleted_at == null);
}

function addDuplicateWarning(warnings: ValidationMessage[], row: RowRecord, fields: string[], message: string) {
  const label = [row.first_name, row.last_name].filter(Boolean).join(' ') || String(row.facility_name || row.id || 'existing record');
  warnings.push({ severity: 'warning', message: `${message} Match: ${label}. Fields: ${fields.join(', ')}.`, matchId: String(row.id || ''), matchLabel: label });
}

export function validatePlayerForm(form: RowRecord, context: EntityValidationContext = {}): ValidationResult {
  const normalized: RowRecord = { ...form };
  const errors: ValidationMessage[] = [];
  const warnings: ValidationMessage[] = [];

  validateName('first_name', 'First Name', normalized, errors);
  validateName('last_name', 'Last Name', normalized, errors);
  normalized.email = normalizeEmail(normalized.email) || null;
  if (!isValidEmailAddress(normalized.email)) errors.push({ field: 'email', severity: 'error', message: 'Enter a valid email address, such as name@example.com.' });
  normalized.mobile_phone = normalizePhone(normalized.mobile_phone) || null;
  if (!isValidUsPhone(normalized.mobile_phone)) errors.push({ field: 'mobile_phone', severity: 'error', message: 'Enter a valid 10-digit U.S. mobile phone number.' });
  normalized.groupme_display_name = String(normalized.groupme_display_name ?? '').trim() || null;
  if (normalized.groupme_display_name && String(normalized.groupme_display_name).length > 120) errors.push({ field: 'groupme_display_name', severity: 'error', message: 'GroupMe Display Name must be 120 characters or fewer.' });

  const birthday = dateOnly(normalized.birthday);
  normalized.birthday = birthday || null;
  if (birthday) {
    if (!isRealDateOnly(birthday)) errors.push({ field: 'birthday', severity: 'error', message: 'Enter a real birthday.' });
    else if (birthday > new Date().toISOString().slice(0, 10)) errors.push({ field: 'birthday', severity: 'error', message: 'Birthday cannot be in the future.' });
    else {
      const age = birthdayAge(birthday);
      if (age !== null && age < 18) warnings.push({ field: 'birthday', severity: 'warning', message: 'This birthday indicates the player is under 18. Confirm that it is correct.' });
      if (age !== null && age > 100) warnings.push({ field: 'birthday', severity: 'warning', message: 'This birthday indicates the player is over 100. Confirm that it is correct.' });
    }
  }

  normalized.address_line1 = collapseWhitespace(normalized.address_line1) || null;
  normalized.address_line2 = collapseWhitespace(normalized.address_line2) || null;
  normalized.city = collapseWhitespace(normalized.city) || null;
  normalized.state = normalizeState(normalized.state) || null;
  normalized.zip_code = normalizeZip(normalized.zip_code) || null;
  if (normalized.address_line1 && !hasMeaningfulText(normalized.address_line1)) errors.push({ field: 'address_line1', severity: 'error', message: 'Address Line 1 must include address text.' });
  validateCity('city', normalized, errors);
  if (normalized.state && !isValidState(normalized.state)) errors.push({ field: 'state', severity: 'error', message: 'Choose a valid state or territory.' });
  if (normalized.zip_code && !isValidZip(normalized.zip_code)) errors.push({ field: 'zip_code', severity: 'error', message: 'Enter a valid ZIP Code, such as 12345 or 12345-6789.' });

  validateRating('utr_rating', 'Overall UTR Rating', normalized, errors, 0, 16.5);
  validateRating('utr_singles_rating', 'UTR Singles Rating', normalized, errors, 0, 16.5);
  validateRating('utr_doubles_rating', 'UTR Doubles Rating', normalized, errors, 0, 16.5);
  validateRating('estimated_dynamic_rating', 'Estimated Dynamic Rating', normalized, errors, 1, 7);
  normalized.ntrp_rating = normalized.ntrp_rating === '' || normalized.ntrp_rating == null ? null : Number(normalized.ntrp_rating);
  if (normalized.ntrp_rating != null && !ntrpValues.has(Number(normalized.ntrp_rating).toFixed(1))) errors.push({ field: 'ntrp_rating', severity: 'error', message: 'Choose a supported NTRP rating.' });

  ['estimated_dynamic_rating_source_url', 'utr_profile_url', 'tennis_record_profile_url', 'usta_profile_url'].forEach((field) => validateUrlField(field, normalized[field], normalized, errors, warnings));
  normalized.sex = String(normalized.sex || '');
  if (!['male', 'female'].includes(normalized.sex)) errors.push({ field: 'sex', severity: 'error', message: 'Choose a valid gender.' });
  normalized.singles_preference = ['yes', 'if_possible', 'no'].includes(String(normalized.singles_preference || '')) ? normalized.singles_preference : 'if_possible';
  normalized.notes = String(normalized.notes ?? '').trim() || null;
  if (normalized.notes && String(normalized.notes).length > 5000) errors.push({ field: 'notes', severity: 'error', message: 'Notes must be 5,000 characters or fewer.' });
  if (normalized.notes && unsafeTextPattern.test(String(normalized.notes))) errors.push({ field: 'notes', severity: 'error', message: 'Notes cannot include script content.' });

  const existing = duplicateRows(context.existingRows || [], context.currentId);
  existing.forEach((row) => {
    const matched: string[] = [];
    if (normalized.email && normalizeEmail(row.email) === normalized.email) matched.push('email');
    if (normalized.mobile_phone && normalizedPhoneKey(row.mobile_phone) === normalizedPhoneKey(normalized.mobile_phone)) matched.push('phone');
    if (normalized.groupme_display_name && collapseWhitespace(row.groupme_display_name).toLowerCase() === String(normalized.groupme_display_name).toLowerCase()) matched.push('GroupMe name');
    ['tennis_record_profile_url', 'utr_profile_url', 'usta_profile_url'].forEach((field) => {
      if (normalized[field] && normalizedUrlKey(row[field]) === normalizedUrlKey(normalized[field])) matched.push(field.replaceAll('_', ' '));
    });
    const sameName = normalizedNameKey(row.first_name, row.last_name) === normalizedNameKey(normalized.first_name, normalized.last_name);
    if (sameName) matched.push('name');
    if (sameName && normalized.birthday && row.birthday === normalized.birthday) matched.push('birthday');
    if (matched.includes('email')) addDuplicateWarning(warnings, row, matched, 'A player with this email address already exists.');
    else if (matched.some((field) => ['phone', 'tennis record profile url', 'utr profile url', 'usta profile url'].includes(field))) addDuplicateWarning(warnings, row, matched, 'A player with matching contact or profile information may already exist.');
    else if (matched.includes('name') && matched.includes('birthday')) addDuplicateWarning(warnings, row, matched, 'A player with the same name and birthday may already exist.');
    else if (matched.includes('name')) addDuplicateWarning(warnings, row, matched, 'A player with the same name may already exist.');
    else if (matched.includes('GroupMe name')) addDuplicateWarning(warnings, row, matched, 'Another active player uses this GroupMe display name.');
  });

  return { normalized, errors, warnings };
}

export function validateFacilityForm(form: RowRecord, context: EntityValidationContext = {}): ValidationResult {
  const normalized: RowRecord = { ...form };
  const errors: ValidationMessage[] = [];
  const warnings: ValidationMessage[] = [];

  // The staging season editor supplies this optional field. Existing facility
  // forms do not acquire a dependency on the staging-only column.
  if (Object.prototype.hasOwnProperty.call(form, 'phone')) {
    normalized.phone = normalizePhone(form.phone) || null;
    if (!isValidUsPhone(normalized.phone)) errors.push({ field: 'phone', severity: 'error', message: 'Enter a valid 10-digit U.S. facility phone number.' });
  }

  normalized.facility_name = collapseWhitespace(normalized.facility_name) || null;
  if (!hasMeaningfulText(normalized.facility_name)) errors.push({ field: 'facility_name', severity: 'error', message: 'Facility Name is required.' });
  else if (String(normalized.facility_name).length > 160) errors.push({ field: 'facility_name', severity: 'error', message: 'Facility Name must be 160 characters or fewer.' });
  normalized.address_line1 = collapseWhitespace(normalized.address_line1) || null;
  normalized.address_line2 = collapseWhitespace(normalized.address_line2) || null;
  normalized.city = collapseWhitespace(normalized.city) || null;
  normalized.state = normalizeState(normalized.state) || null;
  normalized.zip_code = normalizeZip(normalized.zip_code) || null;
  if (normalized.address_line1 && !hasMeaningfulText(normalized.address_line1)) errors.push({ field: 'address_line1', severity: 'error', message: 'Address Line 1 must include address text.' });
  validateCity('city', normalized, errors);
  if (normalized.state && !isValidState(normalized.state)) errors.push({ field: 'state', severity: 'error', message: 'Choose a valid state or territory.' });
  if (normalized.zip_code && !isValidZip(normalized.zip_code)) errors.push({ field: 'zip_code', severity: 'error', message: 'Enter a valid ZIP Code, such as 12345 or 12345-6789.' });
  validateUrlField('google_maps_url', normalized.google_maps_url, normalized, errors, warnings);
  validateUrlField('warmup_booking_url', normalized.warmup_booking_url, normalized, errors, warnings);
  normalized.notes = String(normalized.notes ?? '').trim() || null;
  if (normalized.notes && String(normalized.notes).length > 5000) errors.push({ field: 'notes', severity: 'error', message: 'Notes must be 5,000 characters or fewer.' });
  if (normalized.notes && unsafeTextPattern.test(String(normalized.notes))) errors.push({ field: 'notes', severity: 'error', message: 'Notes cannot include script content.' });

  const existing = duplicateRows(context.existingRows || [], context.currentId);
  existing.forEach((row) => {
    const matched: string[] = [];
    if (normalized.facility_name && collapseWhitespace(row.facility_name).toLowerCase() === String(normalized.facility_name).toLowerCase()) matched.push('name');
    const sameAddress = normalized.address_line1 && normalized.city && normalized.state && normalized.zip_code
      && collapseWhitespace(row.address_line1).toLowerCase() === String(normalized.address_line1).toLowerCase()
      && collapseWhitespace(row.city).toLowerCase() === String(normalized.city).toLowerCase()
      && normalizeState(row.state) === normalized.state
      && normalizeZip(row.zip_code) === normalized.zip_code;
    if (sameAddress) matched.push('address');
    if (normalized.google_maps_url && normalizedUrlKey(row.google_maps_url) === normalizedUrlKey(normalized.google_maps_url)) matched.push('Google Maps URL');
    if (normalized.warmup_booking_url && normalizedUrlKey(row.warmup_booking_url) === normalizedUrlKey(normalized.warmup_booking_url)) matched.push('website');
    if (matched.includes('Google Maps URL') || (matched.includes('name') && matched.includes('address'))) addDuplicateWarning(warnings, row, matched, 'This facility may already exist.');
    else if (matched.length > 0) addDuplicateWarning(warnings, row, matched, 'A similar facility already exists.');
  });

  return { normalized, errors, warnings };
}

export function googleMapsSearchUrl(form: RowRecord): string {
  const parts = [form.facility_name, form.address_line1, form.address_line2, form.city, form.state, form.zip_code]
    .map(collapseWhitespace)
    .filter(Boolean)
    .join(' ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`;
}
